import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import Link from '../../../../components/Link';
import DatePicker from '../../../../components/DatePicker/index';
import { ListFilters } from '../../../../interfaces/property.interface';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import useDebounce from '../../../../hooks/useDebounce';

interface FilterProps {
  constructionStatus: any[];
  allConstructionStatus: any[];
  approvalStatus: any[];
  allApprovalStatus: any[];
  categories: any[];
  allCategories: any[];
  states: any[];
  allStates: any[];
  cities: any[];
  allCities: any[];
  developers: any[];
  allDevelopers: any[];
  amenities: any[];
  allAmenities: any[];
  propertyTypes: any[];
  allPropertyTypes: any[];
  bhkTypes: any[];
  allBhkTypes: any[];
  measurements: any[];
  allMeasurements: any[];
  areas: any[];
  allAreas: any[];
  status: any[];
  allStatus: any[];
  dropdownSearchData: any;
  initialFilters: ListFilters;
  onDropdownSearchChange: (event: any, name: string) => void;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
  users: any[];
  allUsers: any[];
}


const Filters: React.FC<FilterProps> = ({
  constructionStatus,
  allConstructionStatus,
  approvalStatus,
  allApprovalStatus,
  categories,
  allCategories,
  states,
  allStates,
  cities,
  allCities,
  developers,
  allDevelopers,
  amenities,
  allAmenities,
  propertyTypes,
  allPropertyTypes,
  bhkTypes,
  allBhkTypes,
  measurements,
  allMeasurements,
  areas,
  allAreas,
  status,
  allStatus,
  dropdownSearchData,
  initialFilters,
  onDropdownSearchChange,
  onApply,
  onReset,
  users,
  allUsers,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = Boolean(
      filtersToApply.searchText !== '' ||
      filtersToApply.constructionStatus !== '' ||
      filtersToApply.approvalStatus !== '' ||
      filtersToApply.categoryId !== '' ||
      filtersToApply.stateId !== '' ||
      (filtersToApply.cityId && filtersToApply.cityId.length > 0) ||
      (filtersToApply.developerId && filtersToApply.developerId.length > 0) ||
      (filtersToApply.amenityIds && filtersToApply.amenityIds.length > 0) ||
      filtersToApply.propertyTypeId !== '' ||
      (filtersToApply.bhkTypeIds && filtersToApply.bhkTypeIds.length > 0) ||
      (filtersToApply.areaIds && filtersToApply.areaIds.length > 0) ||
      filtersToApply.status !== '1' ||
      filtersToApply.isFeatured !== '' ||
      filtersToApply.possessionDateFrom !== null ||
      filtersToApply.possessionDateTo !== null ||
      filtersToApply.priceMin !== '' ||
      filtersToApply.priceMax !== '' ||
      filtersToApply.reraAreaMin !== '' ||
      filtersToApply.reraAreaMax !== '' ||
      filtersToApply.measurement !== 'sq_ft' ||
      filtersToApply.createdBy !== ''
    );

    setFiltersApplied(applied);
    onApply(filtersToApply);
  }, [
    debouncedSearchText,
    filters.constructionStatus,
    filters.approvalStatus,
    filters.categoryId,
    filters.stateId,
    filters.cityId,
    filters.developerId,
    filters.amenityIds,
    filters.propertyTypeId,
    filters.bhkTypeIds,
    filters.areaIds,
    filters.status,
    filters.isFeatured,
    filters.possessionDateFrom,
    filters.possessionDateTo,
    filters.priceMin,
    filters.priceMax,
    filters.reraAreaMin,
    filters.reraAreaMax,
    filters.measurement,
    filters.createdBy,
    onApply
  ]);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      let value = event.target ? event.target.value : event;
      if (['priceMin', 'priceMax', 'reraAreaMin', 'reraAreaMax'].includes(name) && Number(value) < 0) {
        value = 0;
      }

      setFilters((prev: ListFilters) => {
        const newData: any = {
          ...prev,
          [name]: value,
        };
        if (name === 'stateId') {
          newData['cityId'] = [];
          newData['areaIds'] = [];
        }
        if (name === 'cityId') {
          newData['areaIds'] = [];
        }
        return newData;
      });
    },
    []
  );

  const handleReset = () => {
    setFilters({
      searchText: '',
      constructionStatus: '',
      approvalStatus: '',
      categoryId: '',
      stateId: '',
      cityId: [],
      developerId: [],
      amenityIds: [],
      propertyTypeId: '',
      bhkTypeIds: [],
      areaIds: [],
      status: '1',
      isFeatured: '',
      possessionDateFrom: null,
      possessionDateTo: null,
      priceMin: '',
      priceMax: '',
      reraAreaMin: '',
      reraAreaMax: '',
      measurement: 'sq_ft',
      createdBy: '',
    });
    onReset();
  };

  /* Options Memos - Filtered */
  const constructionStatusOptions = useMemo(() => (constructionStatus || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [constructionStatus]);
  const approvalStatusOptions = useMemo(() => (approvalStatus || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [approvalStatus]);
  const categoryOptions = useMemo(() => (categories || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [categories]);
  const stateOptions = useMemo(() => (states || []).map((state: any) => ({ label: state.name, value: String(state.id) })), [states]);
  const cityOptions = useMemo(() => (cities || []).map((city: any) => ({ label: city.name, value: String(city.id) })), [cities]);
  const developerOptions = useMemo(() => (developers || []).map((dev: any) => ({ label: dev.name, value: String(dev.id) })), [developers]);
  const amenityOptions = useMemo(() => (amenities || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [amenities]);
  const propertyTypeOptions = useMemo(() => (propertyTypes || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [propertyTypes]);
  const bhkTypeOptions = useMemo(() => (bhkTypes || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [bhkTypes]);
  const measurementOptions = useMemo(() => (measurements || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [measurements]);
  const areaOptions = useMemo(() => (areas || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [areas]);
  const statusOptions = useMemo(() => (status || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [status]);
  const featuredOptions = useMemo(() => ([
    { label: t('properties.list.pageTexts.filters.featuredYes'), value: '1' },
    { label: t('properties.list.pageTexts.filters.featuredNo'), value: '0' },
  ]), [t]);

  /* All Options Memos - Full List */
  const allConstructionStatusOptions = useMemo(() => allConstructionStatus?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allConstructionStatus]);
  const allApprovalStatusOptions = useMemo(() => allApprovalStatus?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allApprovalStatus]);
  const allCategoryOptions = useMemo(() => allCategories?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allCategories]);
  const allStateOptions = useMemo(() => allStates?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allStates]);
  const allCityOptions = useMemo(() => allCities?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allCities]);
  const allDeveloperOptions = useMemo(() => allDevelopers?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allDevelopers]);
  const allAmenityOptions = useMemo(() => allAmenities?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allAmenities]);
  const allPropertyTypeOptions = useMemo(() => allPropertyTypes?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allPropertyTypes]);
  const allBhkTypeOptions = useMemo(() => allBhkTypes?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allBhkTypes]);
  const allMeasurementOptions = useMemo(() => allMeasurements?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allMeasurements]);
  const allAreaOptions = useMemo(() => allAreas?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allAreas]);
  const allStatusOptions = useMemo(() => allStatus?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allStatus]);
  const userOptions = useMemo(() => (users || []).map((user: any) => ({ label: user.name, value: String(user.id) })), [users]);
  const allUserOptions = useMemo(() => allUsers?.map((user: any) => ({ label: user.name, value: String(user.id) })), [allUsers]);

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t('common.filters')}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t('common.clearFilters')}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            {/* Row 1: Search, Category, Construction Status, Status */}
            <div className="col-md-5">
              <div className="form-floating form-floating-outline">
                <Input
                  name={VALIDATION_FIELD_MAPPING.searchText}
                  value={filters.searchText}
                  onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.searchText)}
                  placeholder={t('properties.list.pageTexts.filters.searchPlaceholder')}
                  label={t('properties.list.pageTexts.filters.searchPlaceholder')}
                  type="search"
                />
              </div>
              </div>
              <div className="col-md-2">
                <Dropdown
                  id="createdBy"
                  label={t('properties.list.pageTexts.filters.createdBy')}
                  name="createdBy"
                  values={filters.createdBy && filters.createdBy.toString().trim() ? [String(filters.createdBy)] : []}
                  options={userOptions}
                  allOptions={allUserOptions}
                  onChange={(e) => handleInputChange(e, 'createdBy')}
                  placeholder={t('properties.list.pageTexts.filters.createdBy')}
                  onSearch={(e) => onDropdownSearchChange(e, 'user')}
                  searchText={dropdownSearchData.user}
                />
              </div>
              <div className="col-md-2">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.categoryId}
                  label={t('properties.list.pageTexts.filters.category')}
                  name={VALIDATION_FIELD_MAPPING.categoryId}
                  values={filters.categoryId && filters.categoryId.toString().trim() ? [String(filters.categoryId)] : []}
                  options={categoryOptions}
                  allOptions={allCategoryOptions}
                  onChange={(e) => handleInputChange(e, 'categoryId')}
                  placeholder={t('properties.list.pageTexts.filters.selectCategory')}
                  onSearch={(e) => onDropdownSearchChange(e, 'category')}
                  searchText={dropdownSearchData.category}
                />
              </div>
              <div className="col-md-3">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.construction_status}
                  label={t('properties.list.pageTexts.filters.constructionStatus')}
                  name={VALIDATION_FIELD_MAPPING.construction_status}
                  values={filters.constructionStatus && filters.constructionStatus.toString().trim() ? [String(filters.constructionStatus)] : []}
                  options={constructionStatusOptions}
                  allOptions={allConstructionStatusOptions}
                  onChange={(e) => handleInputChange(e, 'constructionStatus')}
                  placeholder={t('properties.list.pageTexts.filters.selectConstructionStatus')}
                  onSearch={(e) => onDropdownSearchChange(e, 'constructionStatus')}
                  searchText={dropdownSearchData.constructionStatus}
                />
              </div>
              <div className="col-md-3">
                <Dropdown
                  id="propertyType"
                  label={t('properties.list.pageTexts.filters.propertyType')}
                  name="propertyType"
                  values={filters.propertyTypeId && filters.propertyTypeId.toString().trim() ? [String(filters.propertyTypeId)] : []}
                  options={propertyTypeOptions}
                  allOptions={allPropertyTypeOptions}
                  onChange={(e) => handleInputChange(e, 'propertyTypeId')}
                  placeholder={t('properties.list.pageTexts.filters.propertyType')}
                  onSearch={(e) => onDropdownSearchChange(e, 'propertyType')}
                  searchText={dropdownSearchData.propertyType}
                />
              </div>

              {/* Row 2: Developer (Multi), Amenities (Multi), Property Type, BHK Type */}
              <div className="col-md-3">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.developerId}
                  label={t('properties.list.pageTexts.filters.developer')}
                  name={VALIDATION_FIELD_MAPPING.developerId}
                  values={filters.developerId || []}
                  options={developerOptions}
                  allOptions={allDeveloperOptions}
                  onChange={(e) => handleInputChange(e, 'developerId')}
                  placeholder={t('properties.list.pageTexts.filters.developer')}
                  onSearch={(e) => onDropdownSearchChange(e, 'developer')}
                  searchText={dropdownSearchData.developer}
                  multiSelect
                />
              </div>
              <div className="col-md-3">
                <Dropdown
                  id="amenities"
                  label={t('properties.list.pageTexts.filters.amenities')}
                  name="amenities"
                  values={filters.amenityIds || []}
                  options={amenityOptions}
                  allOptions={allAmenityOptions}
                  onChange={(e) => handleInputChange(e, 'amenityIds')}
                  placeholder={t('properties.list.pageTexts.filters.amenities')}
                  onSearch={(e) => onDropdownSearchChange(e, 'amenities')}
                  searchText={dropdownSearchData.amenities}
                  multiSelect
                />
              </div>
              <div className="col-md-3">
                <Dropdown
                  id="bhkType"
                  label={t('properties.list.pageTexts.filters.bhkType')}
                  name="bhkType"
                  values={filters.bhkTypeIds || []}
                  options={bhkTypeOptions}
                  allOptions={allBhkTypeOptions}
                  onChange={(e) => handleInputChange(e, 'bhkTypeIds')}
                  placeholder={t('properties.list.pageTexts.filters.bhkType')}
                  onSearch={(e) => onDropdownSearchChange(e, 'bhkType')}
                  searchText={dropdownSearchData.bhkType}
                  multiSelect
                />
              </div>

              {/* Row 3: State, City, Area (Multi) */}
              <div className="col-md-2">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.addressState}
                  label={t('properties.list.pageTexts.filters.state')}
                  name={VALIDATION_FIELD_MAPPING.addressState}
                  values={filters.stateId && filters.stateId.toString().trim() ? [String(filters.stateId)] : []}
                  options={stateOptions}
                  allOptions={allStateOptions}
                  onChange={(e) => handleInputChange(e, 'stateId')}
                  placeholder={t('properties.list.pageTexts.filters.selectState')}
                  onSearch={(e) => onDropdownSearchChange(e, 'state')}
                  searchText={dropdownSearchData.state}
                />
              </div>
              <div className="col-md-2">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.addressCity}
                  label={t('properties.list.pageTexts.filters.city')}
                  name={VALIDATION_FIELD_MAPPING.addressCity}
                  values={filters.cityId || []}
                  options={cityOptions}
                  allOptions={allCityOptions}
                  onChange={(e) => handleInputChange(e, 'cityId')}
                  placeholder={t('properties.list.pageTexts.filters.selectCity')}
                  onSearch={(e) => onDropdownSearchChange(e, 'city')}
                  searchText={dropdownSearchData.city}
                  multiSelect
                />
              </div>
              <div className="col-md-4">
                <Dropdown
                  id="area"
                  label={t('properties.list.pageTexts.filters.area')}
                  name="area"
                  values={filters.areaIds || []}
                  options={areaOptions}
                  allOptions={allAreaOptions}
                  onChange={(e) => handleInputChange(e, 'areaIds')}
                  placeholder={t('properties.list.pageTexts.filters.area')}
                  onSearch={(e) => onDropdownSearchChange(e, 'area')}
                  searchText={dropdownSearchData.area}
                  multiSelect
                />
              </div>
              <div className="col-md-2">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.approval_status}
                  label={t('properties.list.pageTexts.filters.approvalStatus')}
                  name={VALIDATION_FIELD_MAPPING.approval_status}
                  values={filters.approvalStatus && filters.approvalStatus.toString().trim() ? [String(filters.approvalStatus)] : []}
                  options={approvalStatusOptions}
                  allOptions={allApprovalStatusOptions}
                  onChange={(e) => handleInputChange(e, 'approvalStatus')}
                  placeholder={t('properties.list.pageTexts.filters.selectApprovalStatus')}
                  onSearch={(e) => onDropdownSearchChange(e, 'approvalStatus')}
                  searchText={dropdownSearchData.approvalStatus}
                />
              </div>
              <div className="col-md-2">
                <Dropdown
                  id="status"
                  label={t('properties.list.pageTexts.filters.status')}
                  name="status"
                  values={filters.status && filters.status.toString().trim() ? [String(filters.status)] : []}
                  options={statusOptions}
                  allOptions={allStatusOptions}
                  onChange={(e) => handleInputChange(e, 'status')}
                  placeholder={t('properties.list.pageTexts.filters.status')}
                  onSearch={(e) => onDropdownSearchChange(e, 'status')}
                  searchText={dropdownSearchData.status}
                />
              </div>

              {/* Row 4: Possession Date Ranges, Price Ranges */}
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <DatePicker
                    label={t('properties.list.pageTexts.filters.possessionDateStart')}
                    value={filters.possessionDateFrom ? new Date(filters.possessionDateFrom) : null}
                    onChange={(date: Date | null) => handleInputChange({ target: { value: date ? date.toISOString() : '' } }, 'possessionDateFrom')}
                    placeholder={t('properties.list.pageTexts.filters.possessionDateStart')}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <DatePicker
                    label={t('properties.list.pageTexts.filters.possessionDateEnd')}
                    value={filters.possessionDateTo ? new Date(filters.possessionDateTo) : null}
                    onChange={(date: Date | null) => handleInputChange({ target: { value: date ? date.toISOString() : '' } }, 'possessionDateTo')}
                    placeholder={t('properties.list.pageTexts.filters.possessionDateEnd')}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <Input
                    name="priceMin"
                    value={filters.priceMin}
                    onChange={(e) => handleInputChange(e, 'priceMin')}
                    placeholder={t('properties.list.pageTexts.filters.priceMin')}
                    type="number"
                    min="0"
                    label={t('properties.list.pageTexts.filters.priceMin')}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <Input
                    name="priceMax"
                    value={filters.priceMax}
                    onChange={(e) => handleInputChange(e, 'priceMax')}
                    placeholder={t('properties.list.pageTexts.filters.priceMax')}
                    type="number"
                    min="0"
                    label={t('properties.list.pageTexts.filters.priceMax')}
                  />
                </div>
              </div>

              <div className="col-md-4"></div>

              {/* Row 5: RERA Area Ranges, Measurement, Featured */}
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <Input
                    name="reraAreaMin"
                    value={filters.reraAreaMin}
                    onChange={(e) => handleInputChange(e, 'reraAreaMin')}
                    placeholder={t('properties.list.pageTexts.filters.reraAreaMin')}
                    type="number"
                    min="0"
                    label={t('properties.list.pageTexts.filters.reraAreaMin')}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <div className="form-floating form-floating-outline">
                  <Input
                    name="reraAreaMax"
                    value={filters.reraAreaMax}
                    onChange={(e) => handleInputChange(e, 'reraAreaMax')}
                    placeholder={t('properties.list.pageTexts.filters.reraAreaMax')}
                    type="number"
                    min="0"
                    label={t('properties.list.pageTexts.filters.reraAreaMax')}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <Dropdown
                  id="measurement"
                  label={t('properties.addEdit.pageTexts.form.inventoryDetails.measurement')}
                  name="measurement"
                  values={filters.measurement && filters.measurement.toString().trim() ? [String(filters.measurement)] : []}
                  options={measurementOptions}
                  allOptions={allMeasurementOptions}
                  onChange={(e) => handleInputChange(e, 'measurement')}
                  placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.measurement')}
                  onSearch={(e) => onDropdownSearchChange(e, 'measurement')}
                  searchText={dropdownSearchData.measurement}
                />
              </div>
              <div className="col-md-2">
                <Dropdown
                  id="isFeatured"
                  label={t('properties.list.pageTexts.filters.isFeatured')}
                  name="isFeatured"
                  values={filters.isFeatured && filters.isFeatured.toString().trim() ? [String(filters.isFeatured)] : []}
                  options={featuredOptions}
                  allOptions={featuredOptions}
                  onChange={(e) => handleInputChange(e, 'isFeatured')}
                  placeholder={t('properties.list.pageTexts.filters.isFeatured')}
                  noSearch
                />
              </div>


            </div>
          </div>
        </div>
      </div>
  );
};

export default Filters;
